import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  SeedSurvey, 
  SurveyPhoto, 
  Farmer, 
  Governorate, 
  State, 
  Village, 
  Crop, 
  Usage, 
  SampleType, 
  GeneticStatus, 
  CollectionSource, 
  Material, 
  Topography, 
  LocationStatus, 
  SoilTexture, 
  SoilColor, 
  Hardness, 
  Drainage, 
  DiseasePest, 
  SoilPH, 
  WaterPH,
  SurveyStatistics,
  ApiResponse,
  Department,
  Participant,
  SurveyParticipant,
  SurveyFormData,
  AuthResponse,
  LoginCredentials,
  User
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL = '/api';
  
  // Expose HTTP methods for direct use in components
  public get: <T = any>(url: string, config?: any) => Promise<AxiosResponse<T>>;
  public post: <T = any>(url: string, data?: any, config?: any) => Promise<AxiosResponse<T>>;
  public patch: <T = any>(url: string, data?: any, config?: any) => Promise<AxiosResponse<T>>;
  public delete: <T = any>(url: string, config?: any) => Promise<AxiosResponse<T>>;

  constructor() {
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Bind HTTP methods from axios instance
    this.get = this.api.get.bind(this.api);
    this.post = this.api.post.bind(this.api);
    this.patch = this.api.patch.bind(this.api);
    this.delete = this.api.delete.bind(this.api);

    // Add token to requests if available
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Token ${token}`;
      }
      return config;
    });

    // Handle token expiration
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await axios.post(
        `${this.baseURL}/login/`,
        credentials
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get('/auth/user/');
    return response.data;
  }

  // Locations
  async getGovernorates(): Promise<Governorate[]> {
    const response: AxiosResponse<ApiResponse<Governorate>> = await this.api.get('/governorates/');
    return response.data.results;
  }

  async getStates(governorateId?: number): Promise<State[]> {
    const url = governorateId 
      ? `/states/by_governorate/?governorate_id=${governorateId}`
      : '/states/';
    const response: AxiosResponse<any> = await this.api.get(url);
    
    // إذا كان الطلب للولايات حسب المحافظة، البيانات تُرجع كمصفوفة مباشرة
    // إذا كان الطلب لجميع الولايات، البيانات تُرجع في results
    if (governorateId) {
      return response.data; // مصفوفة مباشرة
    } else {
      return response.data.results; // كائن يحتوي على results
    }
  }

  async getVillages(stateId?: number): Promise<Village[]> {
    const url = stateId 
      ? `/villages/by_state/?state_id=${stateId}`
      : '/villages/';
    const response: AxiosResponse<any> = await this.api.get(url);
    
    // نفس المنطق للقرى
    if (stateId) {
      return response.data; // مصفوفة مباشرة
    } else {
      return response.data.results; // كائن يحتوي على results
    }
  }

  // Core Data
  async getParticipants(): Promise<Participant[]> {
    const response: AxiosResponse<ApiResponse<Participant>> = await this.api.get('/seeds/people/');
    return response.data.results;
  }

  async getFarmers(): Promise<Farmer[]> {
    const response: AxiosResponse<ApiResponse<Farmer>> = await this.api.get('/seeds/farmers/');
    return response.data.results;
  }

  async createParticipant(participant: Omit<Participant, 'id' | 'created_at' | 'updated_at'>): Promise<Participant> {
    const response: AxiosResponse<Participant> = await this.api.post('/seeds/people/', participant);
    return response.data;
  }

  async createFarmer(farmer: Omit<Farmer, 'id' | 'created_at' | 'updated_at'>): Promise<Farmer> {
    const response: AxiosResponse<Farmer> = await this.api.post('/seeds/farmers/', farmer);
    return response.data;
  }

  async getCrops(): Promise<Crop[]> {
    const response: AxiosResponse<ApiResponse<Crop>> = await this.api.get('/crops/');
    return response.data.results;
  }

  async getUsages(): Promise<Usage[]> {
    const response: AxiosResponse<ApiResponse<Usage>> = await this.api.get('/usages/');
    return response.data.results;
  }

  async getSampleTypes(): Promise<SampleType[]> {
    const response: AxiosResponse<ApiResponse<SampleType>> = await this.api.get('/sample-types/');
    return response.data.results;
  }

  async getGeneticStatuses(): Promise<GeneticStatus[]> {
    const response: AxiosResponse<ApiResponse<GeneticStatus>> = await this.api.get('/genetic-statuses/');
    return response.data.results;
  }

  async getCollectionSources(): Promise<CollectionSource[]> {
    const response: AxiosResponse<ApiResponse<CollectionSource>> = await this.api.get('/collection-sources/');
    return response.data.results;
  }

  async getMaterials(): Promise<Material[]> {
    const response: AxiosResponse<ApiResponse<Material>> = await this.api.get('/materials/');
    return response.data.results;
  }

  async getTopographies(): Promise<Topography[]> {
    const response: AxiosResponse<ApiResponse<Topography>> = await this.api.get('/topographies/');
    return response.data.results;
  }

  async getLocationStatuses(): Promise<LocationStatus[]> {
    const response: AxiosResponse<ApiResponse<LocationStatus>> = await this.api.get('/location-statuses/');
    return response.data.results;
  }

  async getSoilTextures(): Promise<SoilTexture[]> {
    const response: AxiosResponse<ApiResponse<SoilTexture>> = await this.api.get('/soil-textures/');
    return response.data.results;
  }

  async getSoilColors(): Promise<SoilColor[]> {
    const response: AxiosResponse<ApiResponse<SoilColor>> = await this.api.get('/soil-colors/');
    return response.data.results;
  }

  async getHardnesses(): Promise<Hardness[]> {
    const response: AxiosResponse<ApiResponse<Hardness>> = await this.api.get('/hardnesses/');
    return response.data.results;
  }

  async getDrainages(): Promise<Drainage[]> {
    const response: AxiosResponse<ApiResponse<Drainage>> = await this.api.get('/drainages/');
    return response.data.results;
  }

  async getDiseasePests(): Promise<DiseasePest[]> {
    const response: AxiosResponse<ApiResponse<DiseasePest>> = await this.api.get('/disease-pests/');
    return response.data.results;
  }

  async createDiseasePest(data: { name_ar: string; name_en: string; type: string }): Promise<DiseasePest> {
    const response = await this.api.post('/disease-pests/', data);
    return response.data;
  }

  async updateDiseasePest(id: number, data: { name_ar: string; name_en: string; type: string }): Promise<DiseasePest> {
    const response = await this.api.put(`/disease-pests/${id}/`, data);
    return response.data;
  }

  async deleteDiseasePest(id: number): Promise<void> {
    await this.api.delete(`/disease-pests/${id}/`);
  }

  async getSoilPHs(): Promise<SoilPH[]> {
    const response: AxiosResponse<ApiResponse<SoilPH>> = await this.api.get('/soil-phs/');
    return response.data.results;
  }

  async getWaterPHs(): Promise<WaterPH[]> {
    const response: AxiosResponse<ApiResponse<WaterPH>> = await this.api.get('/water-phs/');
    return response.data.results;
  }

  // Seed Surveys
  async getSurveys(params?: {
    page?: number;
    search?: string;
    crop?: number;
    village?: number;
    participant?: number;
  }): Promise<ApiResponse<SeedSurvey>> {
    const response: AxiosResponse<ApiResponse<SeedSurvey>> = await this.api.get('/seeds/surveys/', {
      params
    });
    return response.data;
  }

  async getSurvey(id: number): Promise<SeedSurvey> {
    const response: AxiosResponse<SeedSurvey> = await this.api.get(`/seeds/surveys/${id}/`);
    return response.data;
  }

  async createSurvey(survey: SurveyFormData): Promise<SeedSurvey> {
    // تحويل البيانات لتتطابق مع الواجهة الخلفية الجديدة
    const surveyData = {
      ...survey,
      // إزالة الحقول غير المطلوبة
    };
    
    // إزالة الحقول التي لا تُرسل للواجهة الخلفية
    delete (surveyData as any).governorate;
    delete (surveyData as any).state;
    
    const response: AxiosResponse<SeedSurvey> = await this.api.post('/seeds/surveys/', surveyData);
    return response.data;
  }

  async updateSurvey(id: number, survey: Partial<SurveyFormData>): Promise<SeedSurvey> {
    const surveyData = {
      ...survey,
    };
    
    // إزالة الحقول غير المطلوبة
    delete (surveyData as any).governorate;
    delete (surveyData as any).state;
    
    const response: AxiosResponse<SeedSurvey> = await this.api.patch(`/seeds/surveys/${id}/`, surveyData);
    return response.data;
  }

  async deleteSurvey(id: number): Promise<void> {
    await this.api.delete(`/seeds/surveys/${id}/`);
  }

  // Survey Photos
  async uploadSurveyPhotos(surveyId: number, photos: File[]): Promise<SurveyPhoto[]> {
    const formData = new FormData();
    photos.forEach((photo, index) => {
      formData.append(`photos`, photo);
    });

    const response: AxiosResponse<SurveyPhoto[]> = await this.api.post(
      `/seeds/surveys/${surveyId}/upload_photos/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async getSurveyPhotos(surveyId: number): Promise<SurveyPhoto[]> {
    const response: AxiosResponse<SurveyPhoto[]> = await this.api.get(`/seeds/surveys/${surveyId}/photos/`);
    return response.data;
  }

  // Statistics
  async getSurveyStatistics(): Promise<SurveyStatistics> {
    const response: AxiosResponse<SurveyStatistics> = await this.api.get('/seeds/surveys/statistics/');
    return response.data;
  }

  // Location-based queries
  async getSurveysByLocation(params: {
    governorate?: number;
    state?: number;
    village?: number;
  }): Promise<SeedSurvey[]> {
    const response: AxiosResponse<ApiResponse<SeedSurvey>> = await this.api.get('/seeds/surveys/by_location/', {
      params
    });
    return response.data.results;
  }

  // Department methods
  async getDepartments(): Promise<Department[]> {
    const response = await this.api.get('/departments/');
    return response.data.results || response.data;
  }

  // Participant methods
  async getParticipantByCivilId(civilid: string): Promise<Participant | null> {
    try {
      const response = await this.api.get(`/seeds/people/by_civilid/?civilid=${civilid}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Farmer methods
  async getFarmerByCivilId(civilid: string): Promise<Farmer | null> {
    try {
      const response = await this.api.get(`/seeds/farmers/by_civilid/?civilid=${civilid}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Survey Form Data - طلب واحد لجميع البيانات المطلوبة للنموذج
  async getSurveyFormData(): Promise<{
    departments: Department[];
    participants: Participant[];
    farmers: Farmer[];
    governorates: Governorate[];
    crops: Crop[];
    usages: Usage[];
    sample_types: SampleType[];
    genetic_statuses: GeneticStatus[];
    collection_sources: CollectionSource[];
    materials: Material[];
    topographies: Topography[];
    location_statuses: LocationStatus[];
    soil_textures: SoilTexture[];
    soil_colors: SoilColor[];
    hardnesses: Hardness[];
    drainages: Drainage[];
    disease_pests: DiseasePest[];
    soil_phs: SoilPH[];
    water_phs: WaterPH[];
  }> {
    const response = await this.api.get('/seeds/form-data/');
    return response.data;
  }

  // Utility methods
  setAuthToken(token: string): void {
    localStorage.setItem('token', token);
  }

  removeAuthToken(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  // Import surveys from Excel
  async importSurveysFromExcel(file: File): Promise<{
    total_rows: number;
    imported: number;
    failed: number;
    errors: Array<{ row: number; error: string }>;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.api.post('/seeds/surveys/import_surveys/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }
}

export default new ApiService();

// Export specific functions for convenience
export const importSurveysFromExcel = (file: File) => 
  new ApiService().importSurveysFromExcel(file);
